import React from 'react'
import {observer} from 'mobx-react-lite'
import {DeliverToHomeIcon, InfoIcon} from '../../../components/icons'
import {Modal} from '../../../components/modal'
import {BaseButton, Box, Button, Flex, Stack, Text, useDisclosure} from '../../../vanilla'
import { useHistory } from 'react-router-dom'
import {useBasketStore, useOrderStore} from '../../../store/hooks/useStore'
import {FormattedNumber} from 'react-intl'
import {DeliverySlot, TimeSlot} from '../../../types/store/basket'
import {useLocation} from 'react-router-dom'
import { Countdown } from '../../../components/book-delivery/reservation-status'
import { DeliveryTimeAndDate } from '../../../components/book-delivery/delivery-time-and-date'

interface BookingConfirmationModalProps extends ReturnType<typeof useDisclosure> {
  slot: TimeSlot | undefined
  deliveryDay: DeliverySlot
  smallBasketEnabled?: boolean
}

export const BookingConfirmationModal = observer(
  ({smallBasketEnabled, deliveryDay, slot, ...rest}: BookingConfirmationModalProps) => {
    const {editMode} = useOrderStore()
    const location = useLocation()
    const {redirectTo}: {redirectTo?: string} = location.state || {}
    const history = useHistory()
    const {shippingAddress, basket, addressDisplay, basketSubTotal} = useBasketStore()

    const returnToCheckout = redirectTo === '/basket'
    const beforeYouGo = redirectTo === '/Iceland-BeforeYouGoPages'
    const redirectShoppingUrl = !redirectTo || (editMode && !returnToCheckout) ? '/offers' : redirectTo
    const redirectShoppingText = beforeYouGo
      ? 'Continue Checkout'
      : editMode || returnToCheckout
      ? 'Checkout to Apply Changes'
      : 'Continue Shopping'

      const handlePreviousPageRedirect = (event: React.MouseEvent | React.TouchEvent)=> {
        if (!redirectShoppingUrl) {
          event?.preventDefault()
          history.goBack()
        }
        else{
          history.push(redirectShoppingUrl)
        }
      }

    function shouldShowAsterisk({
      slot,
      smallBasketEnabled,
      basketSubTotal,
    }: {
      slot?: TimeSlot
      smallBasketEnabled?: boolean
      basketSubTotal?: number
    }) {
      if (
        !smallBasketEnabled ||
        slot?.smallBasketCharge == null ||
        slot?.smallBasketCharge == 0 ||
        !basketSubTotal ||
        slot?.smallBasketChargeThreshold <= basketSubTotal
      ) {
        return {showAsterisk: false, showMessage: false}
      }

      return {
        showAsterisk: true,
        showMessage: true,
        smallBasketChargeThreshold: slot?.smallBasketChargeThreshold,
        smallBasketCharge: slot?.smallBasketCharge,
      }
    }

    const {showAsterisk, showMessage, smallBasketChargeThreshold, smallBasketCharge} =
      shouldShowAsterisk({slot, smallBasketEnabled, basketSubTotal})

      // Check for FREE delivery
      const freeDelivery = slot?.shippingCost === 0

    return (
      <Modal {...rest} size="bookingConfirmation" closeIcon={false} allowScroll={true}>
        <Box>
          <Text variant="heading1" textAlign="center" paddingX="28px" paddingY="16px">
            Your New Delivery Slot
          </Text>
          <Flex
            paddingY="24px"
            paddingX="20px"
            style={{
              background: 'linear-gradient(180deg,#f3f3f3 0,#f3f3f3 50%,#f3f3f3 70%,#fff 0,#fff)',
            }}
            gap="20px"
          >
            <Box style={{minWidth: '86px'}}>
              <Box backgroundColor="accent0Dark" paddingX="4px" paddingY="8px">
                <Text
                  variant="unstyled"
                  fontSize="xs"
                  fontWeight="bold"
                  textAlign="center"
                  color="white"
                  lineHeight="none"
                >
                  {deliveryDay?.dayText}
                </Text>
              </Box>
              <Box
                border="2px"
                borderColor="accent0Dark"
                paddingX="4px"
                paddingY="12px"
                backgroundColor="white"
              >
                <Text textAlign="center" variant="text6">
                  {deliveryDay?.day}
                </Text>
                <Text textAlign="center" variant="text2" lineHeight="none">
                  {deliveryDay?.month}
                </Text>
              </Box>
            </Box>
            <DeliverToHomeIcon paddingRight="12px" style={{width: '100%', height: '55px'}} />
          </Flex>
          <Flex padding="24px" gap={['20px', '40px']} direction={['column', 'column', 'row']}>
            <Stack data-test-selector="delivery-slot-summary" width={['full', 'full', 'half']}>
              <Text color="accent1" variant="text3">
                Your delivery time:
              </Text>
              <Text variant="text3">
                <DeliveryTimeAndDate
                  startHour={slot?.startHour}
                  endHour={slot?.endHour}
                  textProps={{variant: 'text3'}}
                  dayText={deliveryDay?.dayText}
                  showDate={true}
                  />
                </Text>
              <Flex gap="4px" style={{backgroundColor: '#fef9e0'}}>
                <InfoIcon style={{color: '#f3a124', fill: '#f3a124'}} />
                <Text variant="text1">Checkout to apply</Text>
              </Flex>
              <Text variant="text3">
                Delivery:{' '}
                  {freeDelivery ? (
                    <Text variant="text3" as="span" color="accent1">
                      FREE{showAsterisk ? ' *' : ''}
                    </Text>
                  ) : (
                    <Text variant="text3" as="span" color="accent1">
                      <FormattedNumber
                        style="currency"
                        currency={basket?.currency}
                        value={slot?.shippingCost ?? 0}
                      />
                      {showAsterisk ? ' *' : ''}
                    </Text>)}
              </Text>
              {showMessage ? (
                <Text>
                  * Orders below £{smallBasketChargeThreshold} will be charged a small basket fee of
                  £{smallBasketCharge}
                </Text>
              ) : null}
            </Stack>
            <Stack data-test-selector="delivery-address-summary">
              <Text color="accent1" variant="text3">
                {"We'll deliver to:"}
              </Text>
              <Text variant="text3" data-cs-mask="">{shippingAddress?.c_customerAddressID}</Text>
              <Text variant="text3" data-cs-mask="">{addressDisplay}</Text>
            </Stack>
          </Flex>
          <Box paddingY="20px" paddingX="16px" backgroundColor="gray50">
            <Stack spacing="16px">
            <Text variant="text1" textAlign="center">
            This delivery reservation expires in{' '}
            <Countdown slotExpiration={basket?.c_slotExpiryTime || ''} color='accent2' textVariant='text2' />
          </Text>
              <Flex gap="16px" direction={["column-reverse", "column-reverse", "row"]} alignItems="stretch">
                <Button variant="outlineDark" onClick={rest.onClose} size="md" flex={[null, null, "1"]} data-test-selector="edit-time-or-address">
                  Edit Time or Address
                </Button>
                <BaseButton
                  onClick={handlePreviousPageRedirect}
                  data-test-selector="continue-checkout"
                  variant="primary"
                  size="md"
                  flex={[null, null, '1']}
                >
                  <Text
                    variant="unstyled"
                    fontSize="inherit"
                    lineHeight="inherit"
                    fontWeight="inherit"
                  >
                    {redirectShoppingText}
                  </Text>
                </BaseButton>
              </Flex>
            </Stack>
          </Box>
        </Box>
      </Modal>
    )
  },
)

